import type {
  ConversationId,
  ShopperAiTagId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  MongoCustomerId,
  MongoUserId,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type {
  ConversationListItemResponse,
  ConversationPriority,
  ConversationSnoozeOptions,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import type { BoundAction } from "@redotech/redo-web/actions/action-definition";
import type { ActionId } from "@redotech/redo-web/actions/action-ids";
import { useActionScope } from "@redotech/redo-web/actions/action-scope-context";
import {
  conversationActionIds,
  type ConversationActionId,
} from "@redotech/redo-web/actions/conversation-actions/action-ids";
import {
  addTagScope,
  assignScope,
  priorityScope,
  snoozeScope,
} from "@redotech/redo-web/actions/conversation-actions/scope-trees";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { useMemo } from "react";
import { conversationCountsQueryKey } from "../hooks/use-conversation-counts";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";
import { actionRegistry } from "./action-registry";

function invalidateCountsAfterDelay() {
  setTimeout(() => {
    void reactQueryClient.invalidateQueries({
      queryKey: conversationCountsQueryKey,
    });
    void reactQueryClient.invalidateQueries({
      queryKey: ["activeViewStatusCounts"],
    });
  }, 2000);
}

/**
 * A conversation action with a bound execute function.
 */
export type ConversationAction<TParams = void> = BoundAction<
  ConversationListItemResponse,
  ConversationActionId,
  TParams,
  ConversationId
>;

export interface AssignConversationParams {
  assigneeId: MongoUserId | null;
}

export interface AddTagConversationParams {
  tagIds: ShopperAiTagId[];
}

export interface ChangeCustomerConversationParams {
  mongoCustomerId: MongoCustomerId | null;
}

export type SetPriorityParams = ConversationPriority | null;

export type SnoozeParams =
  | { duration: Exclude<ConversationSnoozeOptions, "custom"> }
  | { duration: "custom"; until: Date }
  | null;

function computeSnoozeUntil(
  option: Exclude<ConversationSnoozeOptions, "custom">,
): Date {
  const now = Date.now();
  switch (option) {
    case "1h":
      return new Date(now + 60 * 60 * 1000);
    case "4h":
      return new Date(now + 4 * 60 * 60 * 1000);
    case "24h":
      return new Date(now + 24 * 60 * 60 * 1000);
  }
}

/**
 * Hook that returns the close conversation action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useCloseConversationAction(): ConversationAction {
  const collections = useGlobalConversationCollections();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler((conversationId: ConversationId) => {
    collections.conversationsList.update(conversationId, (draft) => {
      draft.status = "CLOSED";
    });
    invalidateCountsAfterDelay();
  });

  const registryAction = actionRegistry[conversationActionIds.close];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: ConversationListItemResponse) => {
        if (!canClose) return false;
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the reopen conversation action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useReopenConversationAction(): ConversationAction {
  const collections = useGlobalConversationCollections();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler((conversationId: ConversationId) => {
    collections.conversationsList.update(conversationId, (draft) => {
      draft.status = "OPEN";
    });
    invalidateCountsAfterDelay();
  });

  const registryAction = actionRegistry[conversationActionIds.reopen];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: ConversationListItemResponse) => {
        if (!canClose) return false;
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the snooze conversation action with a bound handler.
 */
export function useSnoozeConversationAction(): ConversationAction<SnoozeParams> {
  const collections = useGlobalConversationCollections();

  const execute = useHandler(
    (conversationId: ConversationId, option: SnoozeParams) => {
      if (option === null) {
        // Clear snooze - reopen the conversation
        collections.conversationsList.update(conversationId, (draft) => {
          draft.status = "OPEN";
          draft.snoozedUntil = null;
        });
      } else {
        const snoozedUntil =
          option.duration === "custom"
            ? option.until
            : computeSnoozeUntil(option.duration);
        collections.conversationsList.update(conversationId, (draft) => {
          draft.status = "SNOOZED";
          draft.snoozedUntil = snoozedUntil;
        });
      }
      invalidateCountsAfterDelay();
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.snooze], execute }),
    [execute],
  );
}

export function useSnoozeConversationOptions(): readonly ConversationAction[] {
  const setSnooze = useSnoozeConversationAction();

  return useMemo(
    () => [
      {
        ...actionRegistry[conversationActionIds.snooze1h],
        execute: (conversationId: ConversationId) =>
          setSnooze.execute(conversationId, { duration: "1h" }),
      },
      {
        ...actionRegistry[conversationActionIds.snooze4h],
        execute: (conversationId: ConversationId) =>
          setSnooze.execute(conversationId, { duration: "4h" }),
      },
      {
        ...actionRegistry[conversationActionIds.snooze24h],
        execute: (conversationId: ConversationId) =>
          setSnooze.execute(conversationId, { duration: "24h" }),
      },
      {
        ...actionRegistry[conversationActionIds.snoozeCustom],
        // custom is handled by the modal — this is intentionally a no-op
        execute: (_conversationId: ConversationId) => undefined,
      },
      {
        ...actionRegistry[conversationActionIds.snoozeClear],
        execute: (conversationId: ConversationId) =>
          setSnooze.execute(conversationId, null),
      },
    ],
    [setSnooze],
  );
}

/**
 * Hook that returns the mark read conversation action with a bound handler.
 */
export function useMarkReadConversationAction(): ConversationAction {
  const collections = useGlobalConversationCollections();

  const execute = useHandler((conversationId: ConversationId) => {
    collections.conversationsList.update(conversationId, (draft) => {
      draft.readAt = new Date();
    });
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.markRead], execute }),
    [execute],
  );
}

/**
 * Hook that returns the mark unread conversation action with a bound handler.
 */
export function useMarkUnreadConversationAction(): ConversationAction {
  const collections = useGlobalConversationCollections();

  const execute = useHandler((conversationId: ConversationId) => {
    collections.conversationsList.update(conversationId, (draft) => {
      draft.readAt = null;
    });
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.markUnread], execute }),
    [execute],
  );
}

/**
 * Hook that returns the archive conversation action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useArchiveConversationAction(): ConversationAction {
  const collections = useGlobalConversationCollections();
  const canArchive = useHasPermission(SupportPermissions.ARCHIVE_CONVERSATION);

  const execute = useHandler((conversationId: ConversationId) => {
    collections.conversationsList.update(conversationId, (draft) => {
      draft.status = "ARCHIVED";
    });
    invalidateCountsAfterDelay();
  });

  const registryAction = actionRegistry[conversationActionIds.archive];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: ConversationListItemResponse) => {
        if (!canArchive) return false;
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canArchive],
  );
}

/**
 * Hook that returns the delete conversation action with a bound handler.
 */
export function useDeleteConversationAction(): ConversationAction {
  const collections = useGlobalConversationCollections();

  const execute = useHandler((conversationId: ConversationId) => {
    collections.conversationsList.update(conversationId, (draft) => {
      draft.status = "DELETED";
    });
    invalidateCountsAfterDelay();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.delete], execute }),
    [execute],
  );
}

/**
 * Hook that returns the set priority conversation action with a bound handler.
 */
export function useSetPriorityConversationAction(): ConversationAction<SetPriorityParams> {
  const collections = useGlobalConversationCollections();

  const execute = useHandler(
    (conversationId: ConversationId, priority: SetPriorityParams) => {
      collections.conversationsList.update(conversationId, (draft) => {
        draft.priority = priority;
      });
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.setPriority], execute }),
    [execute],
  );
}

/**
 * Hook that returns all priority option actions with the value baked in.
 * These are void actions that can be executed directly without params.
 */
export function usePriorityOptionActions(): readonly ConversationAction[] {
  const setPriority = useSetPriorityConversationAction();

  return useMemo(
    () => [
      {
        ...actionRegistry[conversationActionIds.priorityUrgent],
        execute: (conversationId: ConversationId) =>
          setPriority.execute(conversationId, "URGENT"),
      },
      {
        ...actionRegistry[conversationActionIds.priorityClear],
        execute: (conversationId: ConversationId) =>
          setPriority.execute(conversationId, null),
      },
    ],
    [setPriority],
  );
}

/**
 * Hook that returns the assign conversation action with a bound handler.
 * Note: This requires the assignee ID to be passed as a param.
 * The actual UI for selecting an assignee should be handled separately.
 */
export function useAssignConversationAction(): ConversationAction<AssignConversationParams> {
  const collections = useGlobalConversationCollections();

  const execute = useHandler(
    (conversationId: ConversationId, params: AssignConversationParams) => {
      collections.conversationsList.update(conversationId, (draft) => {
        draft.mongoAssigneeId = params.assigneeId;
      });
      invalidateCountsAfterDelay();
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.assign], execute }),
    [execute],
  );
}

/**
 * Hook that returns the add tag conversation action with a bound handler.
 * Note: This requires tag IDs to be passed as a param.
 * The actual UI for selecting tags should be handled separately.
 */
export function useAddTagConversationAction(): ConversationAction<AddTagConversationParams> {
  const collections = useGlobalConversationCollections();

  const execute = useHandler(
    (conversationId: ConversationId, params: AddTagConversationParams) => {
      collections.conversationsList.update(conversationId, (draft) => {
        draft.tagIds = params.tagIds;
      });
      invalidateCountsAfterDelay();
    },
  );

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.addTag], execute }),
    [execute],
  );
}

/**
 * Hook that returns the change customer conversation action with a bound handler.
 * Note: This requires the customer ID to be passed as a param.
 * The actual UI for selecting a customer should be handled separately.
 */
export function useChangeCustomerConversationAction(): {
  execute: (
    conversationId: ConversationId,
    params: ChangeCustomerConversationParams,
  ) => void;
} {
  const collections = useGlobalConversationCollections();

  const execute = useHandler(
    (
      conversationId: ConversationId,
      params: ChangeCustomerConversationParams,
    ) => {
      collections.conversationsList.update(conversationId, (draft) => {
        draft.mongoCustomerId = params.mongoCustomerId;
        // Clear customer info optimistically; it will be refreshed on next load
        draft.customer = undefined;
      });
      invalidateCountsAfterDelay();
    },
  );

  return useMemo(() => ({ execute }), [execute]);
}

/**
 * All bound conversation actions.
 */
export interface ConversationActions {
  close: ConversationAction;
  reopen: ConversationAction;
  markInProgress: ConversationAction;
  snooze: ConversationAction<SnoozeParams>;
  markRead: ConversationAction;
  markUnread: ConversationAction;
  archive: ConversationAction;
  delete: ConversationAction;
  setPriority: ConversationAction<SetPriorityParams>;
  assign: ConversationAction<AssignConversationParams>;
  addTag: ConversationAction<AddTagConversationParams>;
}

/**
 * Hook that returns all conversation actions with bound handlers.
 * Useful for command palette, action menus, etc.
 */
export function useConversationActions(): ConversationActions {
  const close = useCloseConversationAction();
  const reopen = useReopenConversationAction();
  const markInProgress = useMarkInProgressConversationAction();
  const snooze = useSnoozeConversationAction();
  const markRead = useMarkReadConversationAction();
  const markUnread = useMarkUnreadConversationAction();
  const archive = useArchiveConversationAction();
  const deleteAction = useDeleteConversationAction();
  const setPriority = useSetPriorityConversationAction();
  const assign = useAssignConversationAction();
  const addTag = useAddTagConversationAction();

  return useMemo(
    () => ({
      close,
      reopen,
      markInProgress,
      snooze,
      markRead,
      markUnread,
      archive,
      delete: deleteAction,
      setPriority,
      assign,
      addTag,
    }),
    [
      close,
      reopen,
      markInProgress,
      snooze,
      markRead,
      markUnread,
      archive,
      deleteAction,
      setPriority,
      assign,
      addTag,
    ],
  );
}

/**
 * Hook that returns the mark in progress conversation action with a bound handler.
 * Includes permission check in isEnabled.
 */
export function useMarkInProgressConversationAction(): ConversationAction {
  const collections = useGlobalConversationCollections();
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);

  const execute = useHandler((conversationId: ConversationId) => {
    collections.conversationsList.update(conversationId, (draft) => {
      draft.status = "IN_PROGRESS";
    });
    invalidateCountsAfterDelay();
  });

  const registryAction = actionRegistry[conversationActionIds.markInProgress];

  return useMemo(
    () => ({
      ...registryAction,
      execute,
      isEnabled: (context: ConversationListItemResponse) => {
        if (!canClose) return false;
        return registryAction.isEnabled?.(context) ?? true;
      },
    }),
    [execute, registryAction, canClose],
  );
}

/**
 * Hook that returns the send message action with a bound handler.
 */
export function useSendMessageAction(
  onSend: () => void | Promise<void>,
): ConversationAction {
  const execute = useHandler(() => {
    void onSend();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.sendMessage], execute }),
    [execute],
  );
}

/**
 * Hook that returns the delete draft action with a bound handler.
 */
export function useDeleteDraftAction(
  onDelete: () => void | Promise<void>,
): ConversationAction {
  const execute = useHandler(() => {
    void onDelete();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.deleteDraft], execute }),
    [execute],
  );
}

/**
 * Hook that returns the reply action with a bound handler.
 */
export function useReplyAction(
  onReply: () => void | Promise<void>,
): ConversationAction {
  const execute = useHandler(() => {
    void onReply();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.reply], execute }),
    [execute],
  );
}

/**
 * Hook that returns the reply all action with a bound handler.
 */
export function useReplyAllAction(
  onReplyAll: () => void | Promise<void>,
): ConversationAction {
  const execute = useHandler(() => {
    void onReplyAll();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.replyAll], execute }),
    [execute],
  );
}

/**
 * Hook that returns the forward action with a bound handler.
 */
export function useForwardAction(
  onForward: () => void | Promise<void>,
): ConversationAction {
  const execute = useHandler(() => {
    void onForward();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.forward], execute }),
    [execute],
  );
}

/**
 * Hook that returns the internal note action with a bound handler.
 */
export function useInternalNoteAction(
  onInternalNote: () => void | Promise<void>,
): ConversationAction {
  const execute = useHandler(() => {
    void onInternalNote();
  });

  return useMemo(
    () => ({ ...actionRegistry[conversationActionIds.internalNote], execute }),
    [execute],
  );
}

/**
 * Type-safe map of action IDs to execute handlers.
 * Handlers are bound to a specific conversation ID, so they take no arguments.
 */
export type ConversationActionHandlerMap = Partial<
  Record<ActionId, () => void>
>;

/**
 * Hook that returns a map of action IDs to handlers bound to a specific conversation.
 * Used by ScopeMenu and keyboard shortcut system to execute actions.
 */
export function useConversationActionHandlers(
  conversationId: ConversationId,
  onMergeTicket?: () => void,
): ConversationActionHandlerMap {
  const {
    close,
    reopen,
    markInProgress,
    snooze,
    markRead,
    markUnread,
    archive,
    delete: deleteAction,
    setPriority,
  } = useConversationActions();

  const { pushScope } = useActionScope();

  return useMemo(
    (): ConversationActionHandlerMap => ({
      // Direct actions
      [conversationActionIds.close]: () => close.execute(conversationId),
      [conversationActionIds.reopen]: () => reopen.execute(conversationId),
      [conversationActionIds.markInProgress]: () =>
        markInProgress.execute(conversationId),
      [conversationActionIds.markRead]: () => markRead.execute(conversationId),
      [conversationActionIds.markUnread]: () =>
        markUnread.execute(conversationId),
      [conversationActionIds.archive]: () => archive.execute(conversationId),
      [conversationActionIds.delete]: () =>
        deleteAction.execute(conversationId),

      [conversationActionIds.mergeTicket]: () => onMergeTicket?.(),

      [conversationActionIds.setPriority]: () => pushScope(priorityScope),
      [conversationActionIds.assign]: () => pushScope(assignScope),
      [conversationActionIds.addTag]: () => pushScope(addTagScope),
      [conversationActionIds.snooze]: () => pushScope(snoozeScope),

      [conversationActionIds.priorityUrgent]: () =>
        setPriority.execute(conversationId, "URGENT"),
      [conversationActionIds.priorityClear]: () =>
        setPriority.execute(conversationId, null),

      // Snooze options
      [conversationActionIds.snooze1h]: () =>
        snooze.execute(conversationId, { duration: "1h" }),
      [conversationActionIds.snooze4h]: () =>
        snooze.execute(conversationId, { duration: "4h" }),
      [conversationActionIds.snooze24h]: () =>
        snooze.execute(conversationId, { duration: "24h" }),
      // snoozeCustom is handled by the modal in ConversationActionsDropdown
      [conversationActionIds.snoozeCustom]: () => undefined,
      [conversationActionIds.snoozeClear]: () =>
        snooze.execute(conversationId, null),

      // Table navigation - these don't operate on a single conversation
      // They should be handled separately by the table component
    }),
    [
      conversationId,
      close,
      reopen,
      markInProgress,
      snooze,
      markRead,
      markUnread,
      archive,
      deleteAction,
      setPriority,
      pushScope,
      onMergeTicket,
    ],
  );
}
